import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { AlertTriangle, Ban, Check, ChevronRight, Info, Link2, Loader2, PackagePlus, Plus, Search, Shapes, Truck, Undo2 } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  LINE_KIND_LABEL,
  asideKind,
  duplicateChargeLines,
  isMiscLine,
  lineAmount,
  type CatalogProduct,
  type LineCandidate,
  type LineKind,
  type NewProductInput,
  type OrderLine,
  type ReceiptDetail,
  type ReceiptLine,
  type ReviewIssue,
} from "./api"
import { BottomSheet } from "./ui"

/**
 * As linhas do documento (Fase 2d): no computador uma tabela (documento ·
 * Odoo · qtd · unit. · desc. · ecotaxa · IVA · total, e as séries por baixo),
 * no telemóvel um cartão por linha que abre a linha completa. Os avisos de
 * cada linha ficam na própria linha.
 */

const MATCH_LABEL: Record<string, string> = { codigo: "pelo código do fornecedor", sugestao: "sugestão", manual: "escolhido à mão" }
const eur = (v: unknown) =>
  v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString("pt-PT", { style: "currency", currency: "EUR" })
const num = (v: unknown) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString("pt-PT"))

export interface LineHandlers {
  onMatch: (lineIndex: number, purchaseLineId: number | null) => void
  /** Artigo do catálogo do Odoo, fora das encomendas escolhidas (0.36.0). */
  onMatchProduct: (lineIndex: number, productId: number) => void
  /** Pesquisa no catálogo do Odoo (referência, nome, EAN, código do fornecedor). */
  searchCatalog: (q: string) => Promise<CatalogProduct[]>
  onAccept: (lineIndex: number) => void
  onSaveBarcode: (lineIndex: number, save: boolean) => void
  onSerials: (lineIndex: number, serials: string[]) => void
  /** «Não é um artigo…» (portes ou ignorar), ou `null` para desfazer (0.41.0). */
  onKind: (lineIndex: number, kind: LineKind | null) => void
  /** «Lançar como Diversos» (true) ou desfazer (false), 0.54.0. */
  onMisc: (lineIndex: number, on: boolean) => void
  /** «Criar artigo no Odoo» (0.54.0): lança o erro para o diálogo o mostrar. */
  createProduct: (lineIndex: number, input: NewProductInput) => Promise<void>
  /** As categorias de artigos do Odoo (para o diálogo). */
  productCategories: () => Promise<Array<{ id: number; name: string }>>
}

interface LinesProps extends LineHandlers {
  detail: ReceiptDetail
  issues: ReviewIssue[]
  notices: Array<{ line_index: number; message: string }>
  editable: boolean
  verified: ReadonlySet<string>
  /** Linha realçada (veio de «Antes de confirmar»). */
  highlight: number | null
}

function discountText(l: ReceiptLine): { main: string; sub?: string } {
  if (l.discount_chain) {
    return l.discount_chain.includes("+") ? { main: `${l.discount_chain}`, sub: `= ${Number(l.discount_percent)}%` } : { main: `${l.discount_chain}%` }
  }
  if (l.discount_percent !== null && l.discount_percent !== undefined && Number(l.discount_percent) !== 0) return { main: `${Number(l.discount_percent)}%` }
  return { main: "—" }
}

// ─── Computador ─────────────────────────────────────────────────────────────

export function LinesTable(props: LinesProps) {
  const { detail, issues, notices, editable, verified, highlight } = props
  const extra = new Map((detail.doc_lines ?? []).map((d) => [d.line_index, d]))
  // A ecotaxa por linha quase nunca vem (vai para os encargos): a coluna só aparece quando há.
  const showEco = (detail.doc_lines ?? []).some((d) => d.eco_fee)
  const cols = showEco ? 8 : 7
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <tr className="border-b">
            <th className="py-2 pr-3">No documento</th>
            <th className="px-3 py-2">No Odoo</th>
            <th className="px-1.5 py-2 text-right">Qtd</th>
            <th className="px-1.5 py-2 text-right">Unit.</th>
            <th className="px-1.5 py-2 text-right">Desc.</th>
            {showEco && (
              <th className="px-1.5 py-2 text-right" title="Ecotaxa">
                Eco.
              </th>
            )}
            <th className="px-1.5 py-2 text-right">IVA</th>
            <th className="py-2 pl-2 text-right">Total</th>
          </tr>
        </thead>
        {detail.lines.map((l) => {
          const lineIssues = issues.filter((i) => i.line_index === l.line_index)
          const lineNotices = notices.filter((n) => n.line_index === l.line_index)
          const d = discountText(l)
          const x = extra.get(l.line_index)
          const aside = !!asideKind(l)
          return (
            <tbody
              key={l.id}
              id={`linha-${l.line_index}`}
              className={cn(
                "scroll-mt-24 border-b last:border-0",
                highlight === l.line_index && "bg-amber-50/70",
                lineIssues.length > 0 && "border-l-2 border-l-amber-400",
                aside && "bg-muted/40 text-muted-foreground",
              )}
            >
              <tr className="align-top">
                <td className="w-[28%] min-w-[8rem] py-3 pr-3">
                  <div className="font-medium leading-snug [overflow-wrap:anywhere]">{l.description || "(sem descrição)"}</div>
                  {l.supplier_code && <div className="font-mono text-xs text-muted-foreground">{l.supplier_code}</div>}
                </td>
                <td className="w-[34%] min-w-[11rem] px-3 py-3">
                  <OdooMatch line={l} {...props} candidates={detail.line_candidates?.[String(l.line_index)] ?? []} orderLines={detail.order_lines} />
                </td>
                <td className="whitespace-nowrap px-1.5 py-3 text-right tabular-nums">{num(l.quantity)}</td>
                <td className="whitespace-nowrap px-1.5 py-3 text-right tabular-nums">{eur(l.unit_price)}</td>
                <td className="whitespace-nowrap px-1.5 py-3 text-right tabular-nums">
                  {d.main}
                  {d.sub && <div className="text-xs text-muted-foreground">{d.sub}</div>}
                </td>
                {showEco && <td className="whitespace-nowrap px-1.5 py-3 text-right tabular-nums">{x?.eco_fee ? eur(x.eco_fee) : "—"}</td>}
                <td className="whitespace-nowrap px-1.5 py-3 text-right tabular-nums">{x?.vat_rate !== null && x?.vat_rate !== undefined ? `${x.vat_rate}%` : "—"}</td>
                <td className="whitespace-nowrap py-3 pl-2 text-right font-medium tabular-nums">{eur(l.net_total)}</td>
              </tr>
              {!aside && (l.serials.length > 0 || editable || lineIssues.length > 0 || lineNotices.length > 0 || l.barcode_candidates.length > 0) && (
                <tr>
                  <td colSpan={cols} className="pb-3">
                    <div className="space-y-2">
                      {(l.serials.length > 0 || (editable && l.odoo_tracking === "serial")) && (
                        <SerialChips line={l} editable={editable} verified={verified} onSerials={(s) => props.onSerials(l.line_index, s)} />
                      )}
                      <BarcodeOption line={l} editable={editable} onSaveBarcode={(v) => props.onSaveBarcode(l.line_index, v)} />
                      <LineWarnings issues={lineIssues} notices={lineNotices} />
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          )
        })}
      </table>
    </div>
  )
}

// ─── Telemóvel ──────────────────────────────────────────────────────────────

export function LinesCards(props: LinesProps) {
  const { detail, issues, notices, verified, highlight } = props
  const [open, setOpen] = useState<number | null>(null)
  const line = open === null ? null : detail.lines.find((l) => l.line_index === open) ?? null
  return (
    <div className="space-y-2">
      {detail.lines.map((l) => {
        const lineIssues = issues.filter((i) => i.line_index === l.line_index)
        const qty = l.quantity === null ? null : Number(l.quantity)
        const checked = l.serials.filter((s) => verified.has(s)).length
        return (
          <div
            key={l.id}
            id={`linha-${l.line_index}`}
            className={cn(
              "scroll-mt-24 rounded-xl border bg-card",
              lineIssues.length > 0 && "border-amber-300",
              highlight === l.line_index && "ring-2 ring-amber-300",
              asideKind(l) && "bg-muted/40 text-muted-foreground",
            )}
          >
            <button type="button" onClick={() => setOpen(l.line_index)} className="flex w-full items-start gap-2 p-3 text-left active:bg-muted/60">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="font-medium leading-snug">{l.description || "(sem descrição)"}</div>
                <div className="text-sm text-muted-foreground">
                  {num(l.quantity)} un. · {eur(l.net_total)}
                  {l.serials.length > 0 && qty !== null && (
                    <span className={cn("ml-1 font-medium", l.odoo_tracking !== "serial" ? "text-muted-foreground" : l.serials.length === qty ? "text-emerald-700" : "text-amber-700")}>
                      · {l.serials.length} / {qty} séries
                    </span>
                  )}
                  {checked > 0 && <span className="ml-1 text-emerald-700">· ✓ {checked} conferidas</span>}
                </div>
              </div>
              <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
            </button>
            <div className="border-t px-3 py-2.5">
              <OdooMatch line={l} {...props} candidates={detail.line_candidates?.[String(l.line_index)] ?? []} orderLines={detail.order_lines} />
            </div>
            {lineIssues.length > 0 && (
              <div className="border-t px-3 py-2.5">
                <LineWarnings issues={lineIssues} notices={[]} />
              </div>
            )}
          </div>
        )
      })}
      <BottomSheet open={line !== null} onClose={() => setOpen(null)} title={line ? `Linha ${line.line_index + 1}` : ""}>
        {line && (
          <LineFull
            {...props}
            line={line}
            extra={(detail.doc_lines ?? []).find((d) => d.line_index === line.line_index)}
            issues={issues.filter((i) => i.line_index === line.line_index)}
            notices={notices.filter((n) => n.line_index === line.line_index)}
          />
        )}
      </BottomSheet>
    </div>
  )
}

/** A linha completa (telemóvel): a tabela em pares rótulo/valor, as séries e os avisos. */
function LineFull({
  line: l,
  extra,
  issues,
  notices,
  editable,
  verified,
  onSerials,
  onSaveBarcode,
}: LinesProps & {
  line: ReceiptLine
  extra?: { vat_rate: number | null; eco_fee: number | null }
  issues: ReviewIssue[]
  notices: Array<{ line_index: number; message: string }>
}) {
  const d = discountText(l)
  const rows: Array<[string, string]> = [
    ["No documento", [l.supplier_code, l.description].filter(Boolean).join(" · ") || "—"],
    [
      "No Odoo",
      asideKind(l)
        ? `${LINE_KIND_LABEL[asideKind(l)!]} — não é um artigo`
        : isMiscLine(l)
          ? `${l.odoo_product_name ?? "Diversos"} — ${l.description ?? ""}`
          : (l.odoo_product_name ?? "Não casado"),
    ],
    ["Quantidade", num(l.quantity)],
    ["Preço unitário", eur(l.unit_price)],
    ["Desconto", d.sub ? `${d.main} ${d.sub}` : d.main],
    ["Ecotaxa", extra?.eco_fee ? eur(extra.eco_fee) : "—"],
    ["IVA", extra?.vat_rate !== null && extra?.vat_rate !== undefined ? `${extra.vat_rate}%` : "—"],
    ["Total", eur(l.net_total)],
  ]
  return (
    <div className="space-y-4">
      <dl className="divide-y rounded-lg border">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-3 px-3 py-2.5 text-base">
            <dt className="w-32 shrink-0 text-muted-foreground">{k}</dt>
            <dd className="min-w-0 flex-1 break-words text-right font-medium tabular-nums">{v}</dd>
          </div>
        ))}
      </dl>
      {!asideKind(l) && !isMiscLine(l) && (l.serials.length > 0 || (editable && l.odoo_tracking === "serial")) && (
        <SerialChips line={l} editable={editable} verified={verified} onSerials={(s) => onSerials(l.line_index, s)} />
      )}
      {!asideKind(l) && !isMiscLine(l) && <BarcodeOption line={l} editable={editable} onSaveBarcode={(v) => onSaveBarcode(l.line_index, v)} />}
      <LineWarnings issues={issues} notices={notices} />
    </div>
  )
}

// ─── Artigo no Odoo: casado, sugestão, «Parecido com», «Associar existente» ──

function OdooMatch({
  line: l,
  candidates,
  orderLines,
  editable,
  detail,
  onMatch,
  onMatchProduct,
  onAccept,
  onKind,
  onMisc,
  createProduct,
  productCategories,
  searchCatalog,
}: LinesProps & { line: ReceiptLine; candidates: LineCandidate[]; orderLines: OrderLine[] }) {
  const [picking, setPicking] = useState(false)
  const [asking, setAsking] = useState(false)
  const [creating, setCreating] = useState(false)
  const notArticle = (
    <NotArticleDialog
      open={asking}
      line={l}
      detail={detail}
      onClose={() => setAsking(false)}
      onPick={(kind) => {
        setAsking(false)
        onKind(l.line_index, kind)
      }}
    />
  )
  if (asideKind(l)) return <SetAsideLine line={l} detail={detail} editable={editable} onUndo={() => onKind(l.line_index, null)} />
  if (isMiscLine(l)) return <MiscLine line={l} editable={editable} onUndo={() => onMisc(l.line_index, false)} />
  const createDialog = (
    <CreateProductDialog
      open={creating}
      line={l}
      onClose={() => setCreating(false)}
      loadCategories={productCategories}
      onCreate={async (input) => {
        await createProduct(l.line_index, input)
        setCreating(false)
      }}
    />
  )
  const picker = (
    <AssociatePicker
      open={picking}
      onClose={() => setPicking(false)}
      orderLines={orderLines}
      current={l.purchase_line_id}
      currentProduct={l.purchase_line_id ? null : l.odoo_product_id}
      search={searchCatalog}
      onPick={(id) => {
        setPicking(false)
        onMatch(l.line_index, id)
      }}
      onPickProduct={(productId) => {
        setPicking(false)
        onMatchProduct(l.line_index, productId)
      }}
    />
  )
  const offOrder = !!l.odoo_product_id && !l.purchase_line_id
  if (l.odoo_product_id && l.match_method !== "sugestao") {
    return (
      <div className="text-sm">
        <div className="flex items-start gap-1.5">
          {offOrder ? <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> : <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />}
          <span className="font-medium leading-snug [overflow-wrap:anywhere]">{l.odoo_product_name}</span>
        </div>
        {offOrder && (
          <div className="ml-5 mt-0.5">
            <span className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900">Sem encomenda · à espera de encomenda</span>
          </div>
        )}
        <div className="ml-5 text-xs text-muted-foreground">
          {l.match_method ? MATCH_LABEL[l.match_method] : ""}
          {editable && (
            <button type="button" onClick={() => setPicking(true)} className="ml-2 inline-flex min-h-11 min-w-11 items-center underline sm:min-h-8 sm:min-w-0">
              mudar
            </button>
          )}
          {editable && (
            <button type="button" onClick={() => setAsking(true)} className="ml-2 inline-flex min-h-11 items-center underline sm:min-h-8">
              não é um artigo…
            </button>
          )}
        </div>
        {l.match_method === "manual" && l.supplier_code && (
          <div className="ml-5 text-xs text-muted-foreground">Ao confirmar, o código {l.supplier_code} fica gravado para este artigo.</div>
        )}
        {l.odoo_tracking && l.odoo_tracking !== "serial" && l.serials.length > 0 && (
          <div className="ml-5 text-xs text-amber-700">Este artigo ainda não tem rastreio por série no Odoo.</div>
        )}
        {picker}
        {notArticle}
      </div>
    )
  }
  if (l.odoo_product_id && l.match_method === "sugestao") {
    return (
      <div className="space-y-1.5 text-sm">
        <div className="font-medium leading-snug [overflow-wrap:anywhere]">{l.odoo_product_name}</div>
        <div className="text-xs font-medium text-amber-700">Confirme o artigo</div>
        {editable && (
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => onAccept(l.line_index)} className="inline-flex h-11 items-center gap-1 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground sm:h-8">
              <Check className="h-4 w-4" /> É este
            </button>
            <button type="button" onClick={() => setPicking(true)} className="inline-flex h-11 items-center gap-1 rounded-md border px-3 text-sm sm:h-8">
              Outro artigo
            </button>
            <button type="button" onClick={() => setAsking(true)} className="inline-flex h-11 items-center gap-1 rounded-md border px-3 text-sm sm:h-8">
              Não é um artigo…
            </button>
          </div>
        )}
        {picker}
        {notArticle}
      </div>
    )
  }
  const others = candidates.filter((c) => c.purchase_line_id !== l.purchase_line_id)
  const suggested = detail.line_kind_suggestions?.[String(l.line_index)]
  return (
    <div className="space-y-2 text-sm">
      <div className="font-medium text-red-700">Não casado</div>
      {suggested && editable && (
        <div className="space-y-1.5 rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-2 text-sky-900">
          <div className="flex items-start gap-1.5 text-xs">
            <Truck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="min-w-0">Parece portes / transporte — não é um artigo.</span>
          </div>
          <button
            type="button"
            onClick={() => onKind(l.line_index, suggested)}
            className="inline-flex h-11 items-center rounded-md bg-white px-2.5 text-xs font-medium ring-1 ring-sky-300 hover:bg-sky-100 sm:h-8"
          >
            Marcar como portes
          </button>
        </div>
      )}
      {others.length > 0 && (
        <div>
          <div className="mb-1 text-xs text-muted-foreground">Parecido com</div>
          <div className="grid gap-1.5">
            {others.map((c) => (
              <button
                key={c.purchase_line_id}
                type="button"
                disabled={!editable}
                onClick={() => onMatch(l.line_index, c.purchase_line_id)}
                className="flex min-h-11 items-center gap-2 rounded-lg border bg-background px-2.5 py-1.5 text-left hover:border-primary hover:bg-primary/5 disabled:opacity-60 sm:min-h-9"
              >
                <span className="min-w-0 flex-1 text-sm leading-snug [overflow-wrap:anywhere]">{c.product_name ?? c.default_code}</span>
                <span className="shrink-0 rounded bg-muted px-1.5 text-xs font-semibold tabular-nums">{c.percent}%</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {editable && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setPicking(true)} className="inline-flex h-11 items-center gap-1.5 rounded-md border px-3 text-sm font-medium hover:bg-muted sm:h-8">
            <Link2 className="h-4 w-4" /> Associar existente
          </button>
          <button type="button" onClick={() => setCreating(true)} className="inline-flex h-11 items-center gap-1.5 rounded-md border px-3 text-sm font-medium hover:bg-muted sm:h-8">
            <PackagePlus className="h-4 w-4" /> Criar artigo no Odoo…
          </button>
          {detail.misc_code && l.description && (
            <button
              type="button"
              onClick={() => onMisc(l.line_index, true)}
              title={`Vai como ${detail.misc_code} com «${l.description}» por baixo, sem stock`}
              className="inline-flex h-11 items-center gap-1.5 rounded-md border px-3 text-sm font-medium hover:bg-muted sm:h-8"
            >
              <Shapes className="h-4 w-4" /> Lançar como Diversos
            </button>
          )}
          <button type="button" onClick={() => setAsking(true)} className="inline-flex h-11 items-center gap-1.5 rounded-md border px-3 text-sm font-medium hover:bg-muted sm:h-8">
            <Ban className="h-4 w-4" /> Não é um artigo…
          </button>
        </div>
      )}
      {createDialog}
      {picker}
      {notArticle}
    </div>
  )
}

/** O que acontece ao valor da linha (portes ou ignorada), conforme fatura/guia e o artigo dos portes. */
function kindConsequence(kind: LineKind, line: ReceiptLine, detail: ReceiptDetail): string {
  const amount = lineAmount(line)
  const value = amount === null ? "" : ` (${eur(amount)})`
  const guide = detail.document_kind === "guide"
  const code = detail.charge_products?.portes ?? null
  if (kind === "charge_portes") {
    if (guide) return "Numa guia não há fatura: nada se escreve no Odoo por esta linha."
    if (!code) return `Sem artigo de portes nas Configurações: o valor${value} não entra na fatura do Odoo.`
    if (amount === null || amount === 0) return "Sem valor no documento: não entra na fatura do Odoo."
    if (duplicateChargeLines(detail.receipt.charges_json, detail.lines).has(line.line_index)) {
      return `Já está nos encargos do documento${value}: vai uma só vez para a fatura do Odoo (artigo ${code}).`
    }
    return `O valor${value} vai para a fatura do Odoo como encargo (artigo ${code}).`
  }
  if (guide || amount === null || amount === 0) return "Fica de fora: nada se escreve no Odoo por esta linha."
  return `Fica de fora: nada se escreve no Odoo — o valor${value} não entra na fatura (acerte-a no Odoo, se for preciso).`
}

/** Linha posta de lado: «Portes / transporte» ou «Ignorada», aprendida ou não, com «desfazer». */
function SetAsideLine({ line: l, detail, editable, onUndo }: { line: ReceiptLine; detail: ReceiptDetail; editable: boolean; onUndo: () => void }) {
  const kind = asideKind(l) ?? "ignored"
  const learned = l.line_kind_source === "aprendido"
  const Icon = kind === "charge_portes" ? Truck : Ban
  return (
    <div className="space-y-1 text-sm">
      <div className="flex flex-wrap items-center gap-1.5">
        <Icon className="h-4 w-4 shrink-0" />
        <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-xs font-semibold text-foreground">{LINE_KIND_LABEL[kind]}</span>
        {learned && (
          <span className="text-xs">{kind === "charge_portes" ? "reconhecido como portes (aprendido)" : "reconhecida como não sendo um artigo (aprendido)"}</span>
        )}
        {editable && (
          <button type="button" onClick={onUndo} className="inline-flex min-h-11 items-center gap-1 text-xs text-foreground underline sm:min-h-8">
            <Undo2 className="h-3.5 w-3.5" /> desfazer
          </button>
        )}
      </div>
      <p className="text-xs">{kindConsequence(kind, l, detail)}</p>
    </div>
  )
}

/**
 * «Não é um artigo…» (0.41.0): portes / transporte ou ignorar. Diálogo da app
 * por cima de tudo (como o `ConfirmDialog`): fechar é «Cancelar».
 */
/** Linha lançada como Diversos (0.54.0): o artigo Diversos com o nome do documento por baixo. */
function MiscLine({ line: l, editable, onUndo }: { line: ReceiptLine; editable: boolean; onUndo: () => void }) {
  const offOrder = !l.purchase_line_id
  return (
    <div className="space-y-1 text-sm">
      <div className="flex items-start gap-1.5">
        {offOrder ? <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> : <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />}
        <span className="min-w-0 leading-snug [overflow-wrap:anywhere]">
          <span className="font-medium">{l.odoo_product_name ?? "Diversos"}</span>
          <span className="block text-muted-foreground">{l.description}</span>
        </span>
      </div>
      <div className="ml-5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
        {offOrder ? (
          <span className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-900">Sem encomenda · entra em «Criar encomenda»</span>
        ) : (
          <span>Na linha Diversos da encomenda · sem stock, o «Recebido» escreve-se ao confirmar</span>
        )}
        {editable && (
          <button type="button" onClick={onUndo} className="inline-flex min-h-11 items-center gap-1 underline sm:min-h-8">
            <Undo2 className="h-3.5 w-3.5" /> desfazer
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * «Criar artigo no Odoo» (0.54.0): um artigo novo com controlo de stock a
 * partir da linha — nome (o do documento), referência (vazia = a automática do
 * Odoo), número de série, categoria e custo. O código e o preço deste
 * fornecedor ficam na tabela dele; a linha fica associada ao artigo novo.
 */
function CreateProductDialog({
  open,
  line: l,
  onClose,
  onCreate,
  loadCategories,
}: {
  open: boolean
  line: ReceiptLine
  onClose: () => void
  onCreate: (input: NewProductInput) => Promise<void>
  loadCategories: () => Promise<Array<{ id: number; name: string }>>
}) {
  const unit = l.unit_price === null || l.unit_price === undefined || l.unit_price === "" ? null : Number(l.unit_price)
  const discount = Number(l.discount_percent ?? 0) || 0
  const netUnit = unit && unit > 0 ? Math.round(unit * (1 - discount / 100) * 10000) / 10000 : null
  const [name, setName] = useState(l.description ?? "")
  const [code, setCode] = useState("")
  const [serial, setSerial] = useState(l.serials.length > 0)
  const [categ, setCateg] = useState<string>("")
  const [cost, setCost] = useState(netUnit !== null ? String(netUnit) : "")
  const [categories, setCategories] = useState<Array<{ id: number; name: string }> | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!open || categories) return
    loadCategories()
      .then(setCategories)
      .catch(() => setCategories([]))
  }, [open, categories, loadCategories])
  if (!open) return null
  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const price = cost.trim() === "" ? null : Number(cost.replace(",", "."))
      if (price !== null && !(Number.isFinite(price) && price >= 0)) throw new Error("O custo tem de ser um número.")
      await onCreate({ name: name.trim(), default_code: code.trim() || null, tracking: serial ? "serial" : "none", categ_id: categ ? Number(categ) : null, standard_price: price })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  const field = "mt-1 h-12 w-full rounded-lg border bg-background px-3 text-base sm:h-10 sm:text-sm"
  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`criar-artigo-${l.line_index}`}
      onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <div className="max-h-[90vh] w-full max-w-md space-y-3 overflow-y-auto rounded-2xl bg-background p-4 text-left text-foreground shadow-xl">
        <h2 id={`criar-artigo-${l.line_index}`} className="text-lg font-semibold leading-snug">
          Criar artigo no Odoo
        </h2>
        <p className="text-sm text-muted-foreground">
          Linha {l.line_index + 1}: <span className="font-medium text-foreground">{[l.supplier_code, l.description].filter(Boolean).join(" · ") || "(sem descrição)"}</span>.
          Um artigo novo com controlo de stock{l.supplier_code ? `; o código ${l.supplier_code} fica na tabela deste fornecedor` : ""}.
        </p>
        <label className="block">
          <span className="text-sm font-medium">Nome</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={field} />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Referência interna</span>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Vazio = automática do Odoo" autoCapitalize="characters" spellCheck={false} className={`${field} font-mono`} />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Categoria</span>
          <select value={categ} onChange={(e) => setCateg(e.target.value)} className={field} disabled={!categories}>
            <option value="">{categories ? "A do Odoo por omissão" : "A carregar…"}</option>
            {(categories ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium">Custo (€)</span>
          <input value={cost} onChange={(e) => setCost(e.target.value)} inputMode="decimal" className={field} />
        </label>
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input type="checkbox" checked={serial} onChange={(e) => setSerial(e.target.checked)} className="h-5 w-5" />
          Com número de série
        </label>
        {error && <p className="rounded-lg bg-red-50 p-2.5 text-sm text-red-700">{error}</p>}
        <div className="grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || !name.trim()}
            className="flex h-12 items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Criar artigo
          </button>
          <button type="button" onClick={onClose} disabled={busy} className="flex h-12 items-center justify-center rounded-xl border text-base font-medium active:bg-muted">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}

function NotArticleDialog({
  open,
  line: l,
  detail,
  onClose,
  onPick,
}: {
  open: boolean
  line: ReceiptLine
  detail: ReceiptDetail
  onClose: () => void
  onPick: (kind: LineKind) => void
}) {
  if (!open) return null
  const who = l.supplier_code ? `com o código ${l.supplier_code}` : "com esta descrição"
  const supplier = detail.receipt.supplier_partner_name ?? detail.receipt.supplier_name
  const options: Array<{ kind: LineKind; title: string; Icon: typeof Truck }> = [
    { kind: "charge_portes", title: "Portes / transporte", Icon: Truck },
    { kind: "ignored", title: "Ignorar (serviço, taxa, outro)", Icon: Ban },
  ]
  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`nao-artigo-${l.line_index}`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md space-y-3 rounded-2xl bg-background p-4 text-left text-foreground shadow-xl">
        <h2 id={`nao-artigo-${l.line_index}`} className="text-lg font-semibold leading-snug">
          Não é um artigo
        </h2>
        <p className="text-sm text-muted-foreground">
          Linha {l.line_index + 1}: <span className="font-medium text-foreground">{[l.supplier_code, l.description].filter(Boolean).join(" · ") || "(sem descrição)"}</span>. O que é?
        </p>
        <div className="grid gap-2">
          {options.map(({ kind, title, Icon }) => (
            <button
              key={kind}
              type="button"
              onClick={() => onPick(kind)}
              className="flex min-h-14 w-full items-start gap-3 rounded-xl border p-3 text-left hover:border-primary hover:bg-primary/5"
            >
              <Icon className="mt-0.5 h-5 w-5 shrink-0" />
              <span className="min-w-0">
                <span className="block text-base font-semibold">{title}</span>
                <span className="block text-sm text-muted-foreground">{kindConsequence(kind, l, detail)}</span>
              </span>
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {supplier ? `Fica aprendido para ${supplier}` : "Fica aprendido para o fornecedor (quando estiver identificado)"}: o próximo documento {who} já sai assim, com «desfazer».
        </p>
        <button type="button" onClick={onClose} className="flex h-12 w-full items-center justify-center rounded-xl border text-base font-medium active:bg-muted">
          Cancelar
        </button>
      </div>
    </div>
  )
}

const CATALOG_MATCH: Record<CatalogProduct["matched_by"], string> = {
  codigo_fornecedor: "pelo código do fornecedor",
  referencia: "pela referência",
  ean: "pelo código de barras",
  nome: "pelo nome",
}

/**
 * «Associar» (0.36.0): primeiro os artigos das encomendas escolhidas (o mesmo
 * que a escolha manual de sempre), depois «Outros artigos do Odoo» — o
 * catálogo inteiro, pela referência, nome, EAN ou código do fornecedor. Um
 * artigo de fora das encomendas deixa a linha «sem encomenda».
 */
function AssociatePicker({
  open,
  onClose,
  orderLines,
  current,
  currentProduct,
  search,
  onPick,
  onPickProduct,
}: {
  open: boolean
  onClose: () => void
  orderLines: OrderLine[]
  current: number | null
  /** Artigo atual quando está fora das encomendas. */
  currentProduct: number | null
  search: (q: string) => Promise<CatalogProduct[]>
  onPick: (purchaseLineId: number | null) => void
  onPickProduct: (productId: number) => void
}) {
  const [q, setQ] = useState("")
  const [catalog, setCatalog] = useState<CatalogProduct[]>([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  /** Um artigo que casou só pelo nome: pede um toque de confirmação antes de associar. */
  const [confirming, setConfirming] = useState<number | null>(null)
  useEffect(() => {
    if (open) {
      setQ("")
      setCatalog([])
      setSearchError(null)
      setConfirming(null)
    }
  }, [open])
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase()
    return orderLines.filter((o) => !t || [o.product_name, o.default_code, o.supplier_code, o.order_name].some((v) => String(v ?? "").toLowerCase().includes(t)))
  }, [orderLines, q])
  // O catálogo do Odoo: só com 2+ carateres, depois de uma pausa na escrita.
  useEffect(() => {
    const t = q.trim()
    if (!open || t.length < 2) {
      setCatalog([])
      setSearching(false)
      return
    }
    let live = true
    setSearching(true)
    const timer = setTimeout(() => {
      search(t)
        .then((found) => live && (setCatalog(found), setSearchError(null)))
        .catch((e: Error) => live && setSearchError(e.message))
        .finally(() => live && setSearching(false))
    }, 300)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [q, open, search])
  // Um artigo que também está nas encomendas escolhidas liga-se à linha delas.
  const inOrders = new Set(orderLines.map((o) => o.product_id))
  const others = catalog
  return (
    <BottomSheet open={open} onClose={onClose} title="Associar a um artigo">
      <div className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nome, referência, EAN ou código do fornecedor"
            className="h-12 w-full rounded-lg border bg-background pl-10 pr-3 text-base"
          />
        </div>
        <div className="max-h-[55vh] space-y-4 overflow-y-auto">
          <section>
            <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nas encomendas escolhidas</h3>
            <ul className="space-y-1.5">
              {shown.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => onPick(o.id)}
                    className={cn("flex min-h-12 w-full items-center gap-2 rounded-lg border px-3 py-2 text-left hover:bg-muted", o.id === current && "border-primary bg-primary/5")}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">{o.product_name}</span>
                      <span className="block text-xs text-muted-foreground">
                        {o.order_name} · por receber {o.qty_to_receive}
                        {o.tracking === "serial" ? " · com série" : ""}
                      </span>
                    </span>
                    {o.id === current && <Check className="h-4 w-4 text-primary" />}
                  </button>
                </li>
              ))}
              {shown.length === 0 && (
                <li className="p-2 text-sm text-muted-foreground">
                  {orderLines.length === 0 ? "Ainda não há encomenda escolhida." : "Nenhum artigo nas encomendas escolhidas."}
                </li>
              )}
            </ul>
          </section>
          <section>
            <h3 className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Outros artigos do Odoo {searching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            </h3>
            {q.trim().length < 2 ? (
              <p className="p-2 text-sm text-muted-foreground">Escreva para procurar no catálogo do Odoo — referência, nome, EAN ou código do fornecedor.</p>
            ) : searchError ? (
              <p className="p-2 text-sm text-red-700">{searchError}</p>
            ) : (
              <ul className="space-y-1.5">
                {others.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => (p.matched_by === "nome" && confirming !== p.id ? setConfirming(p.id) : onPickProduct(p.id))}
                      className={cn(
                        "flex min-h-12 w-full items-center gap-2 rounded-lg border px-3 py-2 text-left hover:bg-muted",
                        (p.id === currentProduct || confirming === p.id) && "border-primary bg-primary/5",
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">{p.name ?? p.default_code}</span>
                        <span className="block text-xs text-muted-foreground">
                          {CATALOG_MATCH[p.matched_by]}
                          {p.supplier_code ? ` · código no fornecedor ${p.supplier_code}` : ""}
                          {p.tracking === "serial" ? " · com série" : ""}
                          {inOrders.has(p.id) ? " · está nas encomendas escolhidas" : " · fica sem encomenda"}
                        </span>
                      </span>
                      {p.id === currentProduct && <Check className="h-4 w-4 text-primary" />}
                    </button>
                    {confirming === p.id && (
                      <div className="mt-1 flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-900">
                        <span className="flex-1">Casou só pelo nome — confirme que é este artigo (o código do documento fica gravado nele).</span>
                        <button type="button" onClick={() => onPickProduct(p.id)} className="inline-flex h-11 items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground sm:h-8">
                          É este — associar
                        </button>
                        <button type="button" onClick={() => setConfirming(null)} className="inline-flex h-11 items-center rounded-md border bg-white px-3 text-sm sm:h-8">
                          Não
                        </button>
                      </div>
                    )}
                  </li>
                ))}
                {!searching && others.length === 0 && <li className="p-2 text-sm text-muted-foreground">Nenhum artigo armazenável com «{q.trim()}».</li>}
              </ul>
            )}
          </section>
        </div>
        {(current !== null || currentProduct !== null) && (
          <button type="button" onClick={() => onPick(null)} className="h-12 w-full rounded-lg border text-base text-red-700">
            Desassociar
          </button>
        )}
      </div>
    </BottomSheet>
  )
}

// ─── Séries, código de barras, avisos ───────────────────────────────────────

export function SerialChips({
  line: l,
  editable,
  verified,
  onSerials,
}: {
  line: ReceiptLine
  editable: boolean
  verified: ReadonlySet<string>
  onSerials: (serials: string[]) => void
}) {
  const [sheet, setSheet] = useState<number | null>(null)
  const qty = l.quantity === null ? null : Number(l.quantity)
  const checked = l.serials.filter((s) => verified.has(s)).length
  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="font-semibold uppercase tracking-wider text-muted-foreground">Séries</span>
        {qty !== null && (
          <span className={cn("font-semibold tabular-nums", l.odoo_tracking !== "serial" ? "text-muted-foreground" : l.serials.length === qty ? "text-emerald-700" : "text-amber-700")}>
            {l.serials.length} / {qty}
          </span>
        )}
        {checked > 0 && <span className="tabular-nums text-emerald-700">✓ {checked} / {l.serials.length} caixas conferidas</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {l.serials.map((s, i) => (
          <button
            key={`${s}-${i}`}
            type="button"
            disabled={!editable}
            onClick={() => setSheet(i)}
            className={cn(
              "inline-flex min-h-12 max-w-full items-center gap-1 break-all rounded-md border px-2.5 py-1 text-left font-mono text-sm disabled:cursor-default sm:min-h-8 sm:text-xs",
              verified.has(s) ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "bg-muted/60",
            )}
          >
            {verified.has(s) && <Check className="h-3.5 w-3.5 shrink-0" />}
            {s}
          </button>
        ))}
        {editable && (
          <button
            type="button"
            onClick={() => setSheet(-1)}
            className="inline-flex min-h-12 items-center gap-1 rounded-md border border-dashed px-2.5 text-sm text-muted-foreground hover:bg-muted sm:min-h-8 sm:text-xs"
          >
            <Plus className="h-4 w-4" /> Série
          </button>
        )}
      </div>
      <SerialSheet
        open={sheet !== null}
        value={sheet !== null && sheet >= 0 ? l.serials[sheet] ?? "" : ""}
        adding={sheet === -1}
        onClose={() => setSheet(null)}
        onSave={(value) => {
          const v = value.trim()
          if (v) onSerials(sheet === -1 ? [...l.serials, v] : l.serials.map((x, k) => (k === sheet ? v : x)))
          setSheet(null)
        }}
        onRemove={() => {
          if (sheet !== null && sheet >= 0) onSerials(l.serials.filter((_, k) => k !== sheet))
          setSheet(null)
        }}
      />
    </div>
  )
}

/**
 * O EAN que o documento trouxe para a linha (no lugar das séries, ou o único
 * código de uma linha de várias unidades — 0.38.0): ao confirmar, fica gravado
 * no artigo se ele não tiver código de barras no Odoo; a pessoa pode tirar.
 * Um código que o artigo já tem não se troca.
 */
function BarcodeOption({ line: l, editable, onSaveBarcode }: { line: ReceiptLine; editable: boolean; onSaveBarcode: (save: boolean) => void }) {
  if (l.barcode_candidates.length === 0) return null
  const code = l.barcode_candidates[0]!
  if ((l.match_method === "codigo" || l.match_method === "manual") && l.odoo_product_id) {
    if (l.odoo_barcode) {
      return (
        <p className="text-xs text-muted-foreground">
          {l.odoo_barcode === code ? (
            <>
              EAN do documento <span className="font-mono">{code}</span> = o do artigo no Odoo.
            </>
          ) : (
            <>
              EAN do documento <span className="font-mono">{code}</span> — o artigo já tem <span className="font-mono">{l.odoo_barcode}</span> no Odoo; não se troca.
            </>
          )}
        </p>
      )
    }
    return (
      <label className="flex min-h-11 items-start gap-2 text-xs sm:min-h-0">
        <input
          type="checkbox"
          className="mt-0.5 h-5 w-5 shrink-0 sm:h-4 sm:w-4"
          disabled={!editable}
          checked={l.save_barcode !== false}
          onChange={(e) => onSaveBarcode(e.target.checked)}
        />
        <span>
          Ao confirmar, o EAN <span className="font-mono">{code}</span> fica gravado neste artigo (não tem código de barras no Odoo).
        </span>
      </label>
    )
  }
  return (
    <p className="text-xs text-muted-foreground">
      Código de barras do artigo, lido no documento (não é uma série): <span className="font-mono">{l.barcode_candidates.join(", ")}</span> — confirme o artigo
      para o poder gravar.
    </p>
  )
}

function LineWarnings({ issues, notices }: { issues: ReviewIssue[]; notices: Array<{ message: string }> }) {
  if (issues.length === 0 && notices.length === 0) return null
  return (
    <ul className="space-y-1 text-xs">
      {issues.map((i, k) => (
        <li key={`i${k}`} className="flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {i.message}
        </li>
      ))}
      {notices.map((n, k) => (
        <li key={`n${k}`} className="flex items-start gap-1.5 px-2 text-sky-800">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {n.message}
        </li>
      ))}
    </ul>
  )
}

/** Editar uma série: folha inferior com um campo grande (2c). */
function SerialSheet({
  open,
  value,
  adding,
  onClose,
  onSave,
  onRemove,
}: {
  open: boolean
  value: string
  adding: boolean
  onClose: () => void
  onSave: (value: string) => void
  onRemove: () => void
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value, open])
  return (
    <BottomSheet open={open} onClose={onClose} title={adding ? "Acrescentar série" : "Editar série"}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          onSave(draft)
        }}
      >
        <input
          autoFocus
          autoCapitalize="characters"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="h-14 w-full rounded-lg border bg-background px-3 font-mono text-lg"
          placeholder="Nº de série"
          aria-label="Nº de série"
        />
        <div className="grid grid-cols-2 gap-2">
          {adding ? (
            <button type="button" onClick={onClose} className="h-12 rounded-lg border text-base">
              Cancelar
            </button>
          ) : (
            <button type="button" onClick={onRemove} className="h-12 rounded-lg border border-red-200 text-base text-red-700">
              Tirar série
            </button>
          )}
          <button type="submit" disabled={!draft.trim()} className="h-12 rounded-lg bg-primary text-base font-semibold text-primary-foreground disabled:opacity-50">
            {adding ? "Acrescentar" : "Guardar"}
          </button>
        </div>
      </form>
    </BottomSheet>
  )
}

// ─── Encargos ───────────────────────────────────────────────────────────────

export function ChargesBlock({ detail, isAdmin }: { detail: ReceiptDetail; isAdmin: boolean }) {
  // Os do documento e as linhas marcadas «Portes / transporte» (0.41.0) — numa guia não há fatura.
  // Uma linha que repete um encargo do documento (mesmo valor) não conta duas vezes (0.42.6).
  const repeated = duplicateChargeLines(detail.receipt.charges_json, detail.lines)
  const fromLines =
    detail.document_kind === "guide"
      ? []
      : detail.lines
          .filter((l) => l.line_kind === "charge_portes" && !repeated.has(l.line_index))
          .map((l) => ({ kind: "portes", description: `${l.description || "Portes"} (linha ${l.line_index + 1})`, amount: lineAmount(l) }))
  const charges = [...(detail.receipt.charges_json ?? []), ...fromLines].filter((c) => (c.amount ?? 0) !== 0)
  if (charges.length === 0) return null
  const products = detail.charge_products ?? {}
  return (
    <div className="rounded-lg border bg-muted/30 p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Encargos</div>
      <ul className="space-y-1.5 text-sm">
        {charges.map((c, i) => {
          const code = products[c.kind]
          return (
            <li key={i} className="flex flex-wrap items-baseline gap-x-2">
              <span className="min-w-0 flex-1">{c.description ?? c.kind}</span>
              <span className="text-xs text-muted-foreground">
                {code ? (
                  <>
                    → artigo <span className="font-mono">{code}</span>
                  </>
                ) : (
                  <>
                    não lançado (sem artigo)
                    {isAdmin && (
                      <Link to="/armazem/configuracoes" className="ml-1 inline-flex min-h-11 items-center underline sm:min-h-0">
                        configurar
                      </Link>
                    )}
                  </>
                )}
              </span>
              <span className="font-medium tabular-nums">{eur(c.amount)}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
